import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Plus, Pencil, Trash2, LogOut, ImagePlus, X, Loader2, ExternalLink } from "lucide-react";
import { Logo } from "../../components/Logo";
import { AdminHeader } from "../../components/AdminHeader";
import { adminApi, clearToken, getToken } from "../../lib/adminApi";

const EMPTY = { title: "", location: "", developer: "", beds: "", oldPrice: "", newPrice: "", currency: "AED", drop: "", reason: "", description: "", image: "", published: true };
const field = "w-full rounded-xl border border-navy/12 bg-white px-4 py-2.5 text-sm text-navy outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/25";

export default function AdminDeals() {
  const nav = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed, else EMPTY or a deal
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.listDeals();
      setDeals(data);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Could not load deals");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!getToken()) { nav("/admin"); return; }
    load();
  }, [load, nav]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2MB). Try a smaller photo."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Please add a title"); return; }
    setSaving(true);
    try {
      if (form.id) await adminApi.updateDeal(form.id, form);
      else await adminApi.createDeal(form);
      toast.success(form.id ? "Deal updated" : "Deal published");
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    try {
      await adminApi.deleteDeal(id);
      toast.success("Deal deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => { clearToken(); nav("/admin"); };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-deals">
      <AdminHeader />

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-semibold text-navy">Distress Deals</h1>
            <p className="mt-1 text-sm text-muted">Publish and manage the deals shown on your website.</p>
          </div>
          <button onClick={() => setForm({ ...EMPTY })} data-testid="admin-new-deal" className="btn-primary"><Plus size={16} /> New deal</button>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-gold" size={30} /></div>
        ) : deals.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="admin-empty">
            <p className="font-display text-xl font-semibold text-navy">No deals yet</p>
            <p className="mt-2 text-sm text-muted">Click “New deal” to publish your first below-market opportunity.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {deals.map((d) => (
              <div key={d.id} className="overflow-hidden rounded-3xl bg-white shadow-soft" data-testid={`admin-deal-${d.id}`}>
                <div className="relative aspect-[16/10] bg-sand">
                  {d.image ? <img src={d.image} alt={d.title} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted"><ImagePlus size={28} /></div>}
                  {d.drop && <span className="absolute right-3 top-3 rounded-full bg-red-500 px-2.5 py-1 text-xs font-semibold text-white">−{d.drop}</span>}
                  {!d.published && <span className="absolute left-3 top-3 rounded-full bg-navy/80 px-2.5 py-1 text-xs font-medium text-white">Draft</span>}
                </div>
                <div className="p-5">
                  <p className="text-xs text-muted">{d.location} {d.developer && `· ${d.developer}`}</p>
                  <h3 className="mt-1 font-display text-lg font-semibold text-navy">{d.title}</h3>
                  <p className="mt-1 text-sm"><span className="text-muted line-through">{d.oldPrice}</span> <span className="font-semibold text-gold">{d.newPrice} {d.currency}</span></p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => setForm(d)} data-testid={`admin-edit-${d.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-navy/15 py-2 text-xs font-medium text-navy transition hover:bg-navy hover:text-white"><Pencil size={13} /> Edit</button>
                    <button onClick={() => remove(d.id)} data-testid={`admin-delete-${d.id}`} className="flex items-center justify-center rounded-full border border-red-200 px-3 py-2 text-red-500 transition hover:bg-red-500 hover:text-white"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {form && (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/40 backdrop-blur-sm" onClick={() => !saving && setForm(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="admin-deal-form"
            className="h-full w-full max-w-xl overflow-y-auto bg-cream p-6 sm:p-8 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-semibold text-navy">{form.id ? "Edit deal" : "New deal"}</h2>
              <button type="button" onClick={() => setForm(null)} className="rounded-full p-2 text-navy hover:bg-navy/5"><X size={22} /></button>
            </div>

            <label className="mt-6 block text-xs font-medium text-navy/70">Photo</label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-24 w-32 shrink-0 overflow-hidden rounded-xl bg-sand">
                {form.image ? <img src={form.image} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted"><ImagePlus size={22} /></div>}
              </div>
              <label className="btn-ghost cursor-pointer">
                <ImagePlus size={15} /> Upload
                <input type="file" accept="image/*" className="hidden" data-testid="admin-image" onChange={onImage} />
              </label>
            </div>

            <label className="mt-5 block text-xs font-medium text-navy/70">Title *</label>
            <input className={field} data-testid="admin-title" value={form.title} onChange={set("title")} placeholder="Marina Gate — High Floor" />

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-medium text-navy/70">Location</label><input className={field} value={form.location} onChange={set("location")} placeholder="Dubai Marina" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Developer</label><input className={field} value={form.developer} onChange={set("developer")} placeholder="Emaar" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Beds / size</label><input className={field} value={form.beds} onChange={set("beds")} placeholder="2 Bed · 1,340 sqft" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Currency</label><input className={field} value={form.currency} onChange={set("currency")} /></div>
              <div><label className="block text-xs font-medium text-navy/70">Original price</label><input className={field} value={form.oldPrice} onChange={set("oldPrice")} placeholder="3,100,000" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Deal price</label><input className={field} value={form.newPrice} onChange={set("newPrice")} placeholder="2,480,000" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Discount (auto if blank)</label><input className={field} value={form.drop} onChange={set("drop")} placeholder="20%" /></div>
              <div><label className="block text-xs font-medium text-navy/70">Reason / tag</label><input className={field} value={form.reason} onChange={set("reason")} placeholder="Motivated seller" /></div>
            </div>

            <label className="mt-4 block text-xs font-medium text-navy/70">Description</label>
            <textarea rows={3} className={`${field} resize-none`} data-testid="admin-description" value={form.description} onChange={set("description")} placeholder="Short description of the deal..." />

            <label className="mt-4 flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="admin-published" className="h-4 w-4 accent-gold" />
              Published (visible on website)
            </label>

            <button type="submit" disabled={saving} data-testid="admin-save" className="btn-primary mt-6 w-full disabled:opacity-60">
              {saving ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : form.id ? "Update deal" : "Publish deal"}
            </button>
          </form>
        </div>
      )}

      <Toaster position="top-center" richColors />
    </div>
  );
}
